import { useState, useEffect, useCallback } from 'react';
import { Plus, MapPin, X, Loader2, Edit2, CheckCircle, XCircle, FileText, BookmarkPlus, Check, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { authFetch } from '../../utils/authFetch';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SENIORITY_OPTIONS = ['Junior', 'Mid-Level', 'Senior', 'Lead', 'Art Director', 'Creative Director', 'Director', 'VP'];
const CONTRACT_OPTIONS  = ['Full-time', 'Freelance', 'Contract', 'Part-time', 'Internship'];

const STATUS_CONFIG = {
  active:  { label: 'Active',  cls: 'bg-green-50 text-green-700 border-green-200',  dot: 'bg-green-500' },
  draft:   { label: 'Draft',   cls: 'bg-zinc-50 text-zinc-500 border-zinc-200',     dot: 'bg-zinc-400' },
  filled:  { label: 'Filled',  cls: 'bg-blue-50 text-blue-700 border-blue-200',     dot: 'bg-blue-500' },
  closed:  { label: 'Closed',  cls: 'bg-red-50 text-red-600 border-red-200',        dot: 'bg-red-400' },
};

// ── Brief Form Modal ──────────────────────────────────────────────────────────
const BriefModal = ({ brief, onSave, onClose }) => {
  const isEdit = !!brief?.id;
  const [form, setForm] = useState({
    role_title:    brief?.role_title    || '',
    seniority:     brief?.seniority     || '',
    location:      brief?.location      || '',
    contract_type: brief?.contract_type || 'Full-time',
    salary_range:  brief?.salary_range  || '',
    description:   brief?.description   || '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async (draft = false) => {
    if (!form.role_title.trim()) { toast.error('Role title is required'); return; }
    setSaving(true);
    await onSave({ ...form, draft }, isEdit ? brief.id : null);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="brief-modal">
      <div className="bg-white w-full max-w-lg border border-border" style={{ maxHeight: '92vh', overflowY: 'auto' }}>
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h3 className="text-sm font-semibold" style={{ fontFamily: "'Bodoni Moda', Georgia, serif" }}>
            {isEdit ? 'Edit Talent Brief' : 'New Talent Brief'}
          </h3>
          <button onClick={onClose}><X className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} /></button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-amber-50 border border-amber-100 px-4 py-3 text-xs text-amber-800 leading-relaxed">
            This brief will be posted on the <strong>Flence job board</strong> — visible to all Flence members. Subscribed members can apply directly.
          </div>

          {/* Role title */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest mb-1.5">
              Role Title <span className="text-red-500">*</span>
            </label>
            <input value={form.role_title} onChange={e => set('role_title', e.target.value)}
              placeholder="e.g. Senior Womenswear Designer"
              className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:border-foreground"
              data-testid="brief-role-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest mb-1.5">Seniority</label>
              <select value={form.seniority} onChange={e => set('seniority', e.target.value)}
                className="w-full border border-border px-3 py-2 text-sm bg-white focus:outline-none focus:border-foreground"
                data-testid="brief-seniority-select">
                <option value="">Any level</option>
                {SENIORITY_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest mb-1.5">Contract Type</label>
              <select value={form.contract_type} onChange={e => set('contract_type', e.target.value)}
                className="w-full border border-border px-3 py-2 text-sm bg-white focus:outline-none focus:border-foreground"
                data-testid="brief-contract-select">
                {CONTRACT_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest mb-1.5">Location</label>
              <input value={form.location} onChange={e => set('location', e.target.value)}
                placeholder="e.g. Milan, Italy"
                className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:border-foreground"
                data-testid="brief-location-input" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest mb-1.5">Budget / Salary</label>
              <input value={form.salary_range} onChange={e => set('salary_range', e.target.value)}
                placeholder="e.g. €45k–€60k / year"
                className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:border-foreground"
                data-testid="brief-salary-input" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest mb-1.5">Description</label>
            <p className="text-xs text-muted-foreground mb-1.5">Subscribed Flence members see this in full. Non-subscribed see first 3 lines blurred.</p>
            <textarea value={form.description} onChange={e => set('description', e.target.value)}
              placeholder="Describe the role, your brand, what you're looking for, and what makes this opportunity compelling..."
              className="w-full border border-border px-3 py-2 text-sm resize-none focus:outline-none focus:border-foreground"
              rows={5} data-testid="brief-description-input" />
          </div>
        </div>

        <div className="px-6 pb-6 flex gap-2">
          <Button onClick={() => handleSave(false)} disabled={saving}
            className="flex-1 rounded-none bg-foreground text-background hover:bg-foreground/80 h-9 text-xs gap-1.5"
            data-testid="brief-publish-btn">
            {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving...</> : <><FileText className="w-3.5 h-3.5" /> {isEdit ? 'Update & Re-publish' : 'Publish to Flence'}</>}
          </Button>
          {!isEdit && (
            <Button onClick={() => handleSave(true)} disabled={saving} variant="outline"
              className="rounded-none h-9 text-xs" data-testid="brief-draft-btn">
              Save Draft
            </Button>
          )}
          <Button onClick={onClose} variant="ghost" className="rounded-none h-9 text-xs">Cancel</Button>
        </div>
      </div>
    </div>
  );
};

// ── Applicant Row ─────────────────────────────────────────────────────────────
const ApplicantRow = ({ app }) => {
  const navigate = useNavigate();
  const c = app.candidate || {};
  const [added, setAdded] = useState(false);
  const [adding, setAdding] = useState(false);
  const hasProfile = !!c.id;

  const handleAddToPipeline = async (e) => {
    e.stopPropagation();
    if (added || !hasProfile) return;
    setAdding(true);
    try {
      const r = await authFetch(`${BACKEND}/api/dashboard/pipeline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidate_id: c.id, stage: 'saved' }),
      });
      if (r.ok) {
        setAdded(true);
        toast.success(`${c.full_name} added to pipeline`);
      } else {
        toast.error('Could not add to pipeline');
      }
    } catch { toast.error('Connection error'); }
    finally { setAdding(false); }
  };

  return (
    <div className="flex items-center gap-3 bg-white border border-border p-2 hover:border-foreground/30 transition-colors">
      {/* Photo — clickable → profile */}
      <button
        onClick={() => hasProfile && navigate(`/candidate/${c.id}`)}
        className={`flex-shrink-0 overflow-hidden bg-secondary ${hasProfile ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
        style={{ width: 32, height: 42 }}
        title={hasProfile ? 'View profile' : ''}
        data-testid={`applicant-photo-${app.id}`}
      >
        {c.photo_url ? (
          <img src={c.photo_url} alt={c.full_name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-xs font-semibold text-muted-foreground/50">
              {c.full_name ? c.full_name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : '?'}
            </span>
          </div>
        )}
      </button>

      {/* Name + role — clickable → profile */}
      <button
        onClick={() => hasProfile && navigate(`/candidate/${c.id}`)}
        className={`flex-1 min-w-0 text-left ${hasProfile ? 'cursor-pointer group' : 'cursor-default'}`}
        data-testid={`applicant-name-${app.id}`}
      >
        <p className={`text-xs font-semibold truncate ${hasProfile ? 'group-hover:underline' : ''}`}>
          {c.full_name || app.candidate_email || 'Unknown applicant'}
        </p>
        <p className="text-xs text-muted-foreground truncate">
          {[c.seniority, c.core_role].filter(Boolean).join(' ') || app.candidate_email || '—'}
        </p>
        {c.overall_ranking && (
          <p className="text-xs text-muted-foreground/60" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            {c.overall_ranking}/10
          </p>
        )}
      </button>

      {/* AI brief-match score */}
      {hasProfile && (
        app.match_score != null ? (
          <div
            className={`flex flex-col items-center justify-center flex-shrink-0 px-2 py-1 border rounded-full ${
              app.match_score >= 70
                ? 'bg-green-50 text-green-700 border-green-200'
                : app.match_score >= 40
                ? 'bg-amber-50 text-amber-700 border-amber-200'
                : 'bg-zinc-50 text-zinc-500 border-zinc-200'
            }`}
            title="AI relevance of this applicant to the brief"
            data-testid={`applicant-match-${app.id}`}
          >
            <span className="text-xs font-bold leading-none" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
              {Math.round(app.match_score)}%
            </span>
            <span className="text-[9px] uppercase tracking-wider leading-none mt-0.5 opacity-70">match</span>
          </div>
        ) : (
          <span
            className="text-xs text-muted-foreground/40 flex-shrink-0 flex items-center gap-1"
            title="Match score is being calculated"
            data-testid={`applicant-match-pending-${app.id}`}
          >
            <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} />
          </span>
        )
      )}

      {/* Applied date */}
      <span className="text-xs text-muted-foreground/60 flex-shrink-0 hidden sm:block">
        {new Date(app.applied_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
      </span>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {hasProfile && (
          <button
            onClick={() => navigate(`/candidate/${c.id}`)}
            className="p-1 text-muted-foreground/40 hover:text-foreground transition-colors"
            title="View full profile"
            data-testid={`applicant-view-${app.id}`}
          >
            <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
          </button>
        )}
        {hasProfile && (
          <button
            onClick={handleAddToPipeline}
            disabled={adding || added}
            className={`flex items-center gap-1 text-xs px-2 py-1 border transition-colors ${
              added
                ? 'border-green-300 bg-green-50 text-green-700'
                : 'border-border hover:border-foreground text-muted-foreground hover:text-foreground'
            }`}
            title={added ? 'In pipeline' : 'Add to pipeline'}
            data-testid={`applicant-add-pipeline-${app.id}`}
          >
            {adding ? (
              <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} />
            ) : added ? (
              <><Check className="w-3 h-3" strokeWidth={2} /> Added</>
            ) : (
              <><BookmarkPlus className="w-3 h-3" strokeWidth={1.5} /> Save</>
            )}
          </button>
        )}
      </div>
    </div>
  );
};


// ── Brief Card ────────────────────────────────────────────────────────────────
const BriefCard = ({ brief, onEdit, onAction, onViewApplicants }) => {
  const [expanded, setExpanded] = useState(false);
  const status  = STATUS_CONFIG[brief.status] || STATUS_CONFIG.active;
  const isActive = brief.status === 'active';

  return (
    <div className="bg-white border border-border" data-testid={`brief-card-${brief.id}`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="font-semibold text-sm" style={{ fontFamily: "'Bodoni Moda', Georgia, serif" }}>
                {brief.role_title}
              </h3>
              <span className={`text-xs px-2 py-0.5 border rounded-full flex items-center gap-1 ${status.cls}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                {status.label}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground mt-1">
              {brief.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" strokeWidth={1.5} />
                  {brief.location}
                </span>
              )}
              {brief.seniority && <span className="px-1.5 py-0.5 bg-secondary border border-border">{brief.seniority}</span>}
              {brief.contract_type && <span className="px-1.5 py-0.5 bg-secondary border border-border">{brief.contract_type}</span>}
              {brief.salary_range && (
                <span className="text-green-700 font-medium">{brief.salary_range}</span>
              )}
            </div>
          </div>

          {/* Applicant count */}
          <button
            onClick={() => { setExpanded(e => !e); onViewApplicants(brief.id); }}
            className={`flex flex-col items-center px-3 py-2 border transition-colors flex-shrink-0 ${(brief.applicant_count || 0) > 0 ? 'border-amber-200 bg-amber-50 hover:bg-amber-100' : 'border-border bg-secondary hover:border-foreground'}`}
            data-testid={`brief-applicants-btn-${brief.id}`}
          >
            <span className="text-lg font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
              {brief.applicant_count || 0}
            </span>
            <span className="text-xs text-muted-foreground">applicant{brief.applicant_count !== 1 ? 's' : ''}</span>
          </button>
        </div>

        {brief.description && (
          <p className="text-xs text-muted-foreground mt-2 line-clamp-2 leading-relaxed">{brief.description}</p>
        )}

        {/* Action row */}
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
          {isActive && (
            <button onClick={() => onEdit(brief)}
              className="flex items-center gap-1 text-xs px-2 py-1 border border-border hover:border-foreground text-muted-foreground hover:text-foreground transition-colors"
              data-testid={`brief-edit-btn-${brief.id}`}>
              <Edit2 className="w-3 h-3" strokeWidth={1.5} /> Edit
            </button>
          )}
          {isActive && (
            <button onClick={() => onAction(brief.id, 'fill')}
              className="flex items-center gap-1 text-xs px-2 py-1 border border-green-200 bg-green-50 hover:bg-green-100 text-green-700 transition-colors"
              data-testid={`brief-fill-btn-${brief.id}`}>
              <CheckCircle className="w-3 h-3" strokeWidth={1.5} /> Mark as Filled
            </button>
          )}
          {isActive && (
            <button onClick={() => onAction(brief.id, 'close')}
              className="flex items-center gap-1 text-xs px-2 py-1 border border-red-100 hover:border-red-300 text-red-500 hover:text-red-700 transition-colors"
              data-testid={`brief-close-btn-${brief.id}`}>
              <XCircle className="w-3 h-3" strokeWidth={1.5} /> Close
            </button>
          )}
          {brief.status === 'draft' && (
            <button onClick={() => onAction(brief.id, 'publish')}
              className="flex items-center gap-1 text-xs px-2 py-1 bg-foreground text-background hover:bg-foreground/80 transition-colors"
              data-testid={`brief-publish-draft-btn-${brief.id}`}>
              <FileText className="w-3 h-3" strokeWidth={1.5} /> Publish
            </button>
          )}
          <span className="ml-auto text-xs text-muted-foreground/50">
            {new Date(brief.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
        </div>
      </div>

      {/* Applicants panel */}
      {expanded && brief._applications && (
        <div className="border-t border-border bg-secondary/20 px-4 py-3">
          {brief._applications.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-2">No applicants yet</p>
          ) : (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-widest mb-2">
                Applicants — click a name to preview, or save to pipeline
              </p>
              {brief._applications.map(app => (
                <ApplicantRow key={app.id} app={app} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const TalentBriefs = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModal, setCreateModal] = useState(false);
  const [editBrief, setEditBrief] = useState(null);
  const isIntelligence = user?.subscription_plan === 'intelligence';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND}/api/briefs`, {});
      const d = await r.json();
      if (d.success) setBriefs(d.data || []);
    } catch { toast.error('Failed to load briefs'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (formData, briefId) => {
    try {
      const r = briefId
        ? await authFetch(`${BACKEND}/api/briefs/${briefId}`, {
            method: 'PATCH', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formData),
          })
        : await authFetch(`${BACKEND}/api/briefs`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formData),
          });
      const d = await r.json();
      if (d.success) {
        toast.success(formData.draft ? 'Brief saved as draft' : briefId ? 'Brief updated' : 'Brief published to Flence');
        setCreateModal(false);
        setEditBrief(null);
        load();
      } else {
        toast.error(d.detail || 'Failed to save brief');
      }
    } catch { toast.error('Failed to save brief'); }
  };

  const handleAction = async (briefId, action) => {
    const labels = { fill: 'Mark as filled?', close: 'Close this brief?' };
    if (!window.confirm(labels[action] || 'Confirm?')) return;
    try {
      const r = await authFetch(`${BACKEND}/api/briefs/${briefId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      const d = await r.json();
      if (d.success) {
        toast.success(action === 'fill' ? 'Brief marked as filled' : action === 'close' ? 'Brief closed' : 'Brief published');
        load();
      }
    } catch { toast.error('Action failed'); }
  };

  const handleViewApplicants = async (briefId) => {
    try {
      const r = await authFetch(`${BACKEND}/api/briefs/${briefId}`, {});
      const d = await r.json();
      if (d.success) {
        const apps = d.data.applications || [];
        setBriefs(prev => prev.map(b => b.id === briefId ? { ...b, _applications: apps } : b));
        // If some applicants are still being scored, re-fetch shortly to show results
        const pending = apps.some(a => a.candidate_id && (a.match_score === null || a.match_score === undefined));
        if (pending) {
          setTimeout(async () => {
            try {
              const r2 = await authFetch(`${BACKEND}/api/briefs/${briefId}`, {});
              const d2 = await r2.json();
              if (d2.success) {
                setBriefs(prev => prev.map(b => b.id === briefId ? { ...b, _applications: d2.data.applications } : b));
              }
            } catch {}
          }, 5000);
        }
      }
    } catch {}
  };

  const active = briefs.filter(b => b.status === 'active');
  const drafts = briefs.filter(b => b.status === 'draft');
  const closed = briefs.filter(b => ['filled', 'closed'].includes(b.status));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight"
            style={{ fontFamily: "'Bodoni Moda', Georgia, serif" }}
            data-testid="briefs-heading">
            Talent Briefs
          </h1>
          <p className="text-xs text-muted-foreground mt-1 uppercase tracking-[0.15em]"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            {active.length} active · {briefs.length} total
          </p>
        </div>
        {isIntelligence ? (
          <Button onClick={() => setCreateModal(true)}
            className="rounded-none bg-foreground text-background hover:bg-foreground/80 gap-1.5 h-9 text-xs flex-shrink-0"
            data-testid="new-brief-btn">
            <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
            New Brief
          </Button>
        ) : (
          <Button onClick={() => navigate('/pricing')} variant="outline"
            className="rounded-none gap-1.5 h-9 text-xs flex-shrink-0">
            Upgrade to INTELLIGENCE
          </Button>
        )}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2].map(i => <div key={i} className="h-28 bg-white border border-border animate-pulse" />)}
        </div>
      ) : briefs.length === 0 ? (
        <div className="text-center py-24 bg-white border border-border" data-testid="briefs-empty">
          <FileText className="w-8 h-8 text-muted-foreground/30 mx-auto mb-4" strokeWidth={1.5} />
          <p className="font-semibold text-sm mb-1">No Talent Briefs yet</p>
          <p className="text-xs text-muted-foreground mb-5 max-w-xs mx-auto">
            Post a brief to advertise open roles on the Flence job board. Subscribed Flence members can apply directly.
          </p>
          {isIntelligence ? (
            <Button onClick={() => setCreateModal(true)} variant="outline" className="rounded-none text-xs h-8">
              Create your first brief
            </Button>
          ) : (
            <Button onClick={() => navigate('/pricing')} className="rounded-none bg-foreground text-background text-xs h-8">
              Upgrade to INTELLIGENCE to post briefs
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {active.length > 0 && (
            <section>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Active</p>
              <div className="space-y-3">
                {active.map(b => (
                  <BriefCard key={b.id} brief={b}
                    onEdit={setEditBrief}
                    onAction={handleAction}
                    onViewApplicants={handleViewApplicants} />
                ))}
              </div>
            </section>
          )}
          {drafts.length > 0 && (
            <section>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Drafts</p>
              <div className="space-y-3">
                {drafts.map(b => (
                  <BriefCard key={b.id} brief={b}
                    onEdit={setEditBrief}
                    onAction={handleAction}
                    onViewApplicants={handleViewApplicants} />
                ))}
              </div>
            </section>
          )}
          {closed.length > 0 && (
            <section>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Closed / Filled</p>
              <div className="space-y-3">
                {closed.map(b => (
                  <BriefCard key={b.id} brief={b}
                    onEdit={setEditBrief}
                    onAction={handleAction}
                    onViewApplicants={handleViewApplicants} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {/* Modals */}
      {createModal && <BriefModal onSave={handleSave} onClose={() => setCreateModal(false)} />}
      {editBrief   && <BriefModal brief={editBrief} onSave={handleSave} onClose={() => setEditBrief(null)} />}
    </div>
  );
};

export default TalentBriefs;
